import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Anti-Cheating Hook for Exam Sessions
 * Monitors: tab switch, window blur/focus, fullscreen exit, split-screen resize,
 * page exit, screen recording attempts, screenshot key combos, right-click,
 * copy/paste, DevTools shortcuts.
 *
 * TECHNICAL LIMITATION: True screenshot/recording blocking is impossible in browsers.
 * This layer deters, detects, and records attempts with watermarks and event monitoring.
 */
export const useAntiCheat = (sessionId, isActive = false, onViolation = null, maxViolations = 2, studentInfo = null, options = {}) => {
  const {
    endpoint = '/api/violations/report',
    idFieldName = 'session_id',
    statusEndpointBuilder = null, // (id) => '/api/...' for syncViolationCount
  } = options;
  const [violationCount, setViolationCount] = useState(0);
  const [showWarning, setShowWarning] = useState(false);
  const [isTerminated, setIsTerminated] = useState(false);
  const [warningMessage, setWarningMessage] = useState('');
  const [isBlurred, setIsBlurred] = useState(false);
  const isActiveRef = useRef(isActive);
  const sessionIdRef = useRef(sessionId);
  const maxViolationsRef = useRef(maxViolations);
  const endpointRef = useRef(endpoint);
  const idFieldRef = useRef(idFieldName);
  const statusBuilderRef = useRef(statusEndpointBuilder);
  const isReportingRef = useRef(false);
  const lastViolationTimeRef = useRef(0);
  const watermarkIntervalRef = useRef(null);
  const blurTimeoutRef = useRef(null);

  // Update refs when props change
  useEffect(() => {
    isActiveRef.current = isActive;
    sessionIdRef.current = sessionId;
    maxViolationsRef.current = maxViolations;
    endpointRef.current = endpoint;
    idFieldRef.current = idFieldName;
    statusBuilderRef.current = statusEndpointBuilder;
  }, [isActive, sessionId, maxViolations, endpoint, idFieldName, statusEndpointBuilder]);

  // Report violation to server - SINGLE SOURCE OF TRUTH
  const reportViolation = useCallback(async (violationType, severity = 'medium') => {
    if (!sessionIdRef.current || !isActiveRef.current) return;
    
    // Debounce: prevent multiple reports within 2 seconds
    const now = Date.now();
    if (now - lastViolationTimeRef.current < 2000) return;
    
    // Prevent concurrent reports
    if (isReportingRef.current) return;
    
    isReportingRef.current = true;
    lastViolationTimeRef.current = now;

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const body = {
        [idFieldRef.current]: sessionIdRef.current,
        violation_type: violationType,
        severity: severity,
      };
      const response = await fetch(`${API_URL}${endpointRef.current}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      const data = await response.json();
      
      // Always use server's violation count - SINGLE SOURCE OF TRUTH
      if (data.violation_count !== undefined) {
        setViolationCount(data.violation_count);
      }

      if (data.should_terminate) {
        setIsTerminated(true);
        setWarningMessage('تم إنهاء الامتحان بسبب تكرار المخالفات.');
        if (onViolation) {
          onViolation('terminated', data.violation_count);
        }
      } else if (data.violation_count < maxViolationsRef.current) {
        setShowWarning(true);
        const remaining = maxViolationsRef.current - data.violation_count;
        const violationLabels = {
          tab_switch: 'التبديل بين التبويبات',
          focus_loss: 'مغادرة نافذة الاختبار',
          fullscreen_exit: 'الخروج من وضع الشاشة الكاملة',
          split_screen: 'تقسيم الشاشة',
          screenshot_attempt: 'محاولة التقاط صورة للشاشة',
          screen_record_attempt: 'محاولة تسجيل الشاشة',
        };
        const label = violationLabels[violationType] || 'مخالفة';
        setWarningMessage(
          `تحذير: تم رصد ${label}. هذا السلوك غير مسموح أثناء الاختبار.\nمتبقي ${remaining} ${remaining === 1 ? 'تحذير' : 'تحذيرات'} قبل إنهاء الاختبار تلقائياً.`
        );
        if (onViolation) {
          onViolation('warning', data.violation_count);
        }
        // Auto-hide warning after 6 seconds
        setTimeout(() => setShowWarning(false), 6000);
      }

      // Blur screen for 3 seconds on screenshot attempt
      if (violationType === 'screenshot_attempt') {
        triggerScreenBlur();
      }

      return data;
    } catch (error) {
      console.error('Error reporting violation:', error);
    } finally {
      isReportingRef.current = false;
    }
  }, [onViolation]);

  // Trigger screen blur for 3 seconds
  const triggerScreenBlur = useCallback(() => {
    setIsBlurred(true);
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
    }
    blurTimeoutRef.current = setTimeout(() => {
      setIsBlurred(false);
    }, 3000);
  }, []);

  // Handle visibility change (tab switch)
  const handleVisibilityChange = useCallback(() => {
    if (document.hidden && isActiveRef.current && !isTerminated) {
      reportViolation('tab_switch', 'high');
    }
  }, [reportViolation, isTerminated]);

  // Handle window blur (switching to another window)
  const handleWindowBlur = useCallback(() => {
    if (isActiveRef.current && !isTerminated) {
      setIsBlurred(true);
      reportViolation('focus_loss', 'medium');
    }
  }, [reportViolation, isTerminated]);

  // Handle window focus
  const handleWindowFocus = useCallback(() => {
    // Only clear blur if not from screenshot attempt
    if (blurTimeoutRef.current === null) {
      setIsBlurred(false);
    }
  }, []);

  // Handle fullscreen exit
  const handleFullscreenChange = useCallback(() => {
    if (isActiveRef.current && !isTerminated) {
      if (!document.fullscreenElement && !document.webkitFullscreenElement && !document.mozFullScreenElement) {
        reportViolation('fullscreen_exit', 'medium');
      }
    }
  }, [reportViolation, isTerminated]);

  // Handle before unload (page refresh/close)
  const handleBeforeUnload = useCallback((e) => {
    if (isActiveRef.current && !isTerminated) {
      e.preventDefault();
      e.returnValue = 'هل أنت متأكد من مغادرة الصفحة؟ سيتم احتساب ذلك كمخالفة.';
      return e.returnValue;
    }
  }, [isTerminated]);

  // Prevent right-click
  const handleContextMenu = useCallback((e) => {
    if (isActiveRef.current) {
      e.preventDefault();
      return false;
    }
  }, []);

  // Prevent copy/cut/paste
  const handleCopyPaste = useCallback((e) => {
    if (isActiveRef.current) {
      e.preventDefault();
      return false;
    }
  }, []);

  // Enhanced keyboard shortcuts blocking with PrintScreen detection
  const handleKeyDown = useCallback((e) => {
    if (isActiveRef.current) {
      // Block common copy/paste shortcuts
      if ((e.ctrlKey || e.metaKey) && ['c', 'v', 'x', 'a', 'p'].includes(e.key.toLowerCase())) {
        e.preventDefault();
        return false;
      }
      
      // Block F12 (DevTools)
      if (e.key === 'F12') {
        e.preventDefault();
        return false;
      }
      
      // Block Ctrl+Shift+I (DevTools)
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === 'i') {
        e.preventDefault();
        return false;
      }
      
      // Block Print Screen - MAIN SCREENSHOT DETECTION
      if (e.key === 'PrintScreen') {
        e.preventDefault();
        // Try to clear clipboard
        try {
          navigator.clipboard.writeText('');
        } catch (err) {}
        reportViolation('screenshot_attempt', 'high');
        return false;
      }
      
      // Block Windows + Shift + S (Windows screenshot)
      if (e.metaKey && e.shiftKey && e.key.toLowerCase() === 's') {
        e.preventDefault();
        reportViolation('screenshot_attempt', 'high');
        return false;
      }
      
      // Block Cmd + Shift + 3/4/5 (Mac screenshot)
      if (e.metaKey && e.shiftKey && ['3', '4', '5'].includes(e.key)) {
        e.preventDefault();
        reportViolation('screenshot_attempt', 'high');
        return false;
      }
      
      // Block Windows + G (Game Bar / Screen Recording)
      if (e.metaKey && e.key.toLowerCase() === 'g') {
        e.preventDefault();
        reportViolation('screen_record_attempt', 'high');
        return false;
      }
      
      // Block Ctrl + Shift + S (Some screenshot tools)
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === 's') {
        e.preventDefault();
        reportViolation('screenshot_attempt', 'high');
        return false;
      }
      
      // Block Ctrl + P (Print)
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'p') {
        e.preventDefault();
        return false;
      }
      
      // Block Alt + Print Screen (Active window screenshot)
      if (e.altKey && e.key === 'PrintScreen') {
        e.preventDefault();
        reportViolation('screenshot_attempt', 'high');
        return false;
      }
    }
  }, [reportViolation]);

  // Handle keyup for PrintScreen (some browsers fire on keyup)
  const handleKeyUp = useCallback((e) => {
    if (isActiveRef.current && e.key === 'PrintScreen') {
      e.preventDefault();
      try {
        navigator.clipboard.writeText('');
      } catch (err) {}
      reportViolation('screenshot_attempt', 'high');
      return false;
    }
  }, [reportViolation]);

  // Detect split-screen: window width drops to ≤55% of screen width
  const lastWidthRef = useRef(window.innerWidth);
  const splitScreenTimerRef = useRef(null);
  const handleWindowResize = useCallback(() => {
    if (!isActiveRef.current) return;
    const ratio = window.innerWidth / window.screen.width;
    const widthChanged = Math.abs(window.innerWidth - lastWidthRef.current) > 50;
    lastWidthRef.current = window.innerWidth;
    // Only flag as split-screen if width is significantly narrowed (≤55%) and changed
    if (ratio <= 0.55 && widthChanged) {
      // Debounce: fire once after resize settles
      if (splitScreenTimerRef.current) clearTimeout(splitScreenTimerRef.current);
      splitScreenTimerRef.current = setTimeout(() => {
        reportViolation('split_screen', 'medium');
      }, 800);
    }
  }, [reportViolation]);

  // Intercept navigator.mediaDevices.getDisplayMedia (screen recording / sharing)
  const originalGetDisplayMediaRef = useRef(null);
  const interceptScreenRecording = useCallback(() => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getDisplayMedia) return;
    if (originalGetDisplayMediaRef.current) return; // already intercepted
    originalGetDisplayMediaRef.current = navigator.mediaDevices.getDisplayMedia.bind(navigator.mediaDevices);
    navigator.mediaDevices.getDisplayMedia = async (...args) => {
      if (isActiveRef.current) {
        reportViolation('screen_record_attempt', 'high');
        throw new DOMException('Screen capture is not allowed during exams.', 'NotAllowedError');
      }
      return originalGetDisplayMediaRef.current(...args);
    };
  }, [reportViolation]);

  const restoreGetDisplayMedia = useCallback(() => {
    if (originalGetDisplayMediaRef.current && navigator.mediaDevices) {
      navigator.mediaDevices.getDisplayMedia = originalGetDisplayMediaRef.current;
      originalGetDisplayMediaRef.current = null;
    }
  }, []);

  // Create Dynamic Watermark Overlay
  const createWatermarkOverlay = useCallback(() => {
    if (!isActiveRef.current) return;

    // Remove existing watermark
    const existingOverlay = document.getElementById('anti-cheat-watermark');
    if (existingOverlay) existingOverlay.remove();

    const overlay = document.createElement('div');
    overlay.id = 'anti-cheat-watermark';
    overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
      z-index: 99999;
      overflow: hidden;
      user-select: none;
      -webkit-user-select: none;
      -moz-user-select: none;
      -ms-user-select: none;
    `;

    // Generate watermark content with student info and timestamp
    const studentName = studentInfo?.name || studentInfo?.full_name || 'طالب';
    const deviceId = studentInfo?.device_id || 'N/A';
    const timestamp = new Date().toLocaleTimeString('ar-SA', { 
      hour: '2-digit', 
      minute: '2-digit',
      second: '2-digit'
    });
    
    const watermarkText = `${studentName} • ${deviceId.slice(-6)} • ${timestamp}`;

    // Create multiple watermark instances in a diagonal pattern
    const rows = 15;
    const cols = 8;
    
    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        const watermark = document.createElement('div');
        watermark.textContent = watermarkText;
        watermark.style.cssText = `
          position: absolute;
          color: rgba(128, 128, 128, 0.08);
          font-size: 12px;
          font-family: Arial, sans-serif;
          white-space: nowrap;
          transform: rotate(-35deg);
          user-select: none;
          -webkit-user-select: none;
          pointer-events: none;
          top: ${(row * 100 / rows)}%;
          left: ${(col * 100 / cols) + (row % 2 ? 6 : 0)}%;
        `;
        overlay.appendChild(watermark);
      }
    }

    document.body.appendChild(overlay);
  }, [studentInfo]);

  // Create blur overlay for security warnings
  const createBlurOverlay = useCallback(() => {
    let blurOverlay = document.getElementById('anti-cheat-blur');
    if (!blurOverlay) {
      blurOverlay = document.createElement('div');
      blurOverlay.id = 'anti-cheat-blur';
      blurOverlay.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(255, 255, 255, 0.98);
        backdrop-filter: blur(30px);
        -webkit-backdrop-filter: blur(30px);
        z-index: 999998;
        display: none;
        justify-content: center;
        align-items: center;
        font-size: 24px;
        color: #dc2626;
        text-align: center;
        direction: rtl;
      `;
      blurOverlay.innerHTML = `
        <div>
          <div style="font-size: 64px; margin-bottom: 20px;">⚠️</div>
          <div style="font-weight: bold; margin-bottom: 10px;">تم رصد محاولة غير مشروعة</div>
          <div style="font-size: 16px; color: #666;">يرجى العودة إلى نافذة الاختبار</div>
          <div style="font-size: 14px; margin-top: 15px; color: #999;">تم تسجيل هذه المحاولة</div>
        </div>
      `;
      document.body.appendChild(blurOverlay);
    }
  }, []);

  // Update blur overlay visibility
  useEffect(() => {
    const blurOverlay = document.getElementById('anti-cheat-blur');
    if (blurOverlay) {
      blurOverlay.style.display = isBlurred && isActive ? 'flex' : 'none';
    }
  }, [isBlurred, isActive]);

  // Remove protection elements
  const removeScreenProtection = useCallback(() => {
    const overlay = document.getElementById('anti-cheat-watermark');
    const blurOverlay = document.getElementById('anti-cheat-blur');
    if (overlay) overlay.remove();
    if (blurOverlay) blurOverlay.remove();
  }, []);

  // Add CSS to prevent text selection and drag
  const addProtectionStyles = useCallback(() => {
    let styleElement = document.getElementById('anti-cheat-styles');
    if (!styleElement) {
      styleElement = document.createElement('style');
      styleElement.id = 'anti-cheat-styles';
      styleElement.textContent = `
        body.anti-cheat-active {
          user-select: none !important;
          -webkit-user-select: none !important;
          -moz-user-select: none !important;
          -ms-user-select: none !important;
          -webkit-touch-callout: none !important;
        }
        body.anti-cheat-active img,
        body.anti-cheat-active video {
          pointer-events: none !important;
          -webkit-user-drag: none !important;
          user-drag: none !important;
        }
        @media print {
          body.anti-cheat-active * {
            display: none !important;
          }
        }
      `;
      document.head.appendChild(styleElement);
    }
    document.body.classList.add('anti-cheat-active');
  }, []);

  const removeProtectionStyles = useCallback(() => {
    document.body.classList.remove('anti-cheat-active');
    const styleElement = document.getElementById('anti-cheat-styles');
    if (styleElement) styleElement.remove();
  }, []);

  // Setup event listeners
  useEffect(() => {
    if (isActive && !isTerminated) {
      // Add protection styles
      addProtectionStyles();

      // Create watermark overlay
      createWatermarkOverlay();
      createBlurOverlay();

      // Update watermark every 10 seconds with new timestamp
      watermarkIntervalRef.current = setInterval(createWatermarkOverlay, 10000);

      // Intercept screen recording API
      interceptScreenRecording();

      // Add event listeners
      document.addEventListener('visibilitychange', handleVisibilityChange);
      window.addEventListener('blur', handleWindowBlur);
      window.addEventListener('focus', handleWindowFocus);
      window.addEventListener('beforeunload', handleBeforeUnload);
      window.addEventListener('resize', handleWindowResize);
      document.addEventListener('contextmenu', handleContextMenu);
      document.addEventListener('copy', handleCopyPaste);
      document.addEventListener('cut', handleCopyPaste);
      document.addEventListener('paste', handleCopyPaste);
      document.addEventListener('keydown', handleKeyDown, true);
      document.addEventListener('keyup', handleKeyUp, true);

      // Fullscreen change listeners (cross-browser)
      document.addEventListener('fullscreenchange', handleFullscreenChange);
      document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.addEventListener('mozfullscreenchange', handleFullscreenChange);

      // Cleanup
      return () => {
        removeScreenProtection();
        removeProtectionStyles();
        restoreGetDisplayMedia();
        if (watermarkIntervalRef.current) {
          clearInterval(watermarkIntervalRef.current);
        }
        if (blurTimeoutRef.current) {
          clearTimeout(blurTimeoutRef.current);
        }
        if (splitScreenTimerRef.current) {
          clearTimeout(splitScreenTimerRef.current);
        }
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        window.removeEventListener('blur', handleWindowBlur);
        window.removeEventListener('focus', handleWindowFocus);
        window.removeEventListener('beforeunload', handleBeforeUnload);
        window.removeEventListener('resize', handleWindowResize);
        document.removeEventListener('contextmenu', handleContextMenu);
        document.removeEventListener('copy', handleCopyPaste);
        document.removeEventListener('cut', handleCopyPaste);
        document.removeEventListener('paste', handleCopyPaste);
        document.removeEventListener('keydown', handleKeyDown, true);
        document.removeEventListener('keyup', handleKeyUp, true);
        document.removeEventListener('fullscreenchange', handleFullscreenChange);
        document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
        document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      };
    } else {
      removeScreenProtection();
      removeProtectionStyles();
      restoreGetDisplayMedia();
    }
  }, [
    isActive,
    isTerminated,
    handleVisibilityChange,
    handleWindowBlur,
    handleWindowFocus,
    handleBeforeUnload,
    handleWindowResize,
    handleContextMenu,
    handleCopyPaste,
    handleKeyDown,
    handleKeyUp,
    handleFullscreenChange,
    interceptScreenRecording,
    restoreGetDisplayMedia,
    createWatermarkOverlay,
    createBlurOverlay,
    addProtectionStyles,
    removeProtectionStyles,
    removeScreenProtection,
  ]);

  // Dismiss warning
  const dismissWarning = useCallback(() => {
    setShowWarning(false);
  }, []);

  // Reset state (for new session)
  const reset = useCallback(() => {
    setViolationCount(0);
    setShowWarning(false);
    setIsTerminated(false);
    setWarningMessage('');
    setIsBlurred(false);
  }, []);

  // Fetch current violation count from server
  const syncViolationCount = useCallback(async () => {
    if (!sessionIdRef.current) return;
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const statusUrl = statusBuilderRef.current
        ? statusBuilderRef.current(sessionIdRef.current)
        : `/api/student/exam-sessions/${sessionIdRef.current}/status`;
      const response = await fetch(`${API_URL}${statusUrl}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.violation_count !== undefined) {
          setViolationCount(data.violation_count);
        }
      }
    } catch (error) {
      console.error('Error syncing violation count:', error);
    }
  }, []);

  return {
    violationCount,
    showWarning,
    isTerminated,
    warningMessage,
    isBlurred,
    dismissWarning,
    reset,
    reportViolation,
    syncViolationCount,
  };
};

export default useAntiCheat;
